import { createContext, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = user

  useEffect(() => {
    // Skip the auth probe on public embeddable pages (e.g. /s/:id) to avoid 401 noise.
    if (window.location.pathname.startsWith("/s/")) { setUser(false); return; }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  // Legacy email/password admin login (fallback)
  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  // SocialPulse / Firebase federated sign-in — exchange Firebase ID token for our session
  const firebaseSession = async (idToken, extra = {}) => {
    try {
      const { data } = await api.post("/auth/firebase/session", { id_token: idToken, ...extra });
      setUser(data.user);
      return { ok: true, isNew: data.is_new_user, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.debug("logout request failed (clearing session anyway)", e); }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, firebaseSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
